import { Fragment, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { DotLegalAlert, DotLegalButton, DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import AIDialogDisclaimer from "@common/components/AIDialogDisclaimer";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { defineDocumentReviewPane } from "../../main/panes/documentPane/documentReviewPane";
import { useContractAiUpdatePane } from "./ContractAiUpdatePane.hooks";
import { useContractAiUpdateStyles } from "./ContractAiUpdate.styles";
import ContractAiUpdateFieldRow from "./ContractAiUpdateFieldRow";
import ContractAiUpdateAutoRenewalRow from "./ContractAiUpdateAutoRenewalRow";
import CounterPartiesReviewField from "./CounterPartiesReviewField";
import { ContractStandardField } from "../../entityTemplates/EntityTemplate.types";

export interface ContractAiUpdatePaneProps {
    contractId: string;
    /** Set when the review was opened from a document, which it then scans without asking again. */
    documentId?: string;
}

/**
 * The AI-assisted update as a rail occupant: pick a document, wait for the read, then review every
 * template field with the contract's current value beside the proposed one.
 */
function ContractAiUpdatePane(props: ContractAiUpdatePaneProps) {
    const { translateString } = useTranslation();
    const styles = useContractAiUpdateStyles();
    const [isConfirmingSave, setIsConfirmingSave] = useState(false);
    const state = useContractAiUpdatePane({ contractId: props.contractId, documentId: props.documentId });

    const footer = (
        <>
            {state.step === "pickDocument" && (
                <DotLegalButton
                    buttonType="primary"
                    disabled={!state.documentPicker.selectedDocument}
                    onClick={() => state.startSuggestion(state.documentPicker.selectedDocument!)}
                >
                    {translateString("continue")}
                </DotLegalButton>
            )}

            {state.step === "review" && (
                <DotLegalButton
                    buttonType="primary"
                    isLoading={state.isSaving}
                    onClick={() => {
                        if (!state.trySave()) {
                            return;
                        }

                        if (state.partiesToBeRemoved.length > 0) {
                            setIsConfirmingSave(true);
                        } else {
                            state.save();
                        }
                    }}
                >
                    {translateString("save")}
                </DotLegalButton>
            )}
        </>
    );

    return (
        <PaneLayout
            header={translateString("aiAssistedUpdate")}
            footer={footer}
            footerNote={<AIDialogDisclaimer />}
            fixedContent={
                state.step === "review" && (
                    <Box sx={styles.columnHeadings}>
                        <DotLegalHeader headerStyle="extraSmall" marginBottom={0}>
                            {translateString("currentValue")}
                        </DotLegalHeader>
                        <Box />
                        <DotLegalHeader headerStyle="extraSmall" marginBottom={0}>
                            {translateString("proposedValue")}
                        </DotLegalHeader>
                    </Box>
                )
            }
        >
            <Box ref={state.reviewBodyRef} sx={styles.body}>
                {state.step === "pickDocument" && (
                    <>
                        <DotLegalHeader headerStyle={"small"}>{translateString("aiAssistedUpdateIntro")}</DotLegalHeader>
                        <DotLegalSelect
                            label={translateString("selectDocumentToScan")}
                            placeholder={translateString("selectDocumentToScan")}
                            options={state.documentPicker.documentOptions}
                            selectedItem={state.selectedContractDocumentId ?? null}
                            onChange={(contractDocumentId) => state.setSelectedContractDocumentId(contractDocumentId ?? undefined)}
                            noOptionsLabel={translateString("noOptions")}
                            isLoading={state.documentPicker.isLoading}
                            disableClearable
                        />
                    </>
                )}

                {state.step === "analysing" && (
                    <>
                        <DotLegalAlert
                            warningHeader={translateString("extractingContractHeader")}
                            warningText={translateString("extractingContractInfo")}
                            severity="info"
                            marginBottom={3}
                        />
                        {/* Not DotLegalFullScreenSpinner: its 100% height beside the alert overflows the pane. */}
                        <Box sx={styles.analysingSpinner}>
                            <CircularProgress />
                        </Box>
                    </>
                )}

                {state.step === "review" &&
                    state.review &&
                    state.currentContract &&
                    state.fieldGroups.map((group, groupIndex) => (
                        <Fragment key={group.stepId}>
                            {/* Nearer its own fields than the section above, so it reads as their heading. */}
                            <DotLegalHeader headerStyle="small" marginTop={groupIndex === 0 ? 0 : 2} marginBottom={1}>
                                {group.stepName}
                            </DotLegalHeader>
                            {group.fields.map((field) => {
                                const { canToggle, isShowingCurrentValue } = state.getFieldToggleState(field);

                                return (
                                    <Fragment key={field.id}>
                                        {/* Across both columns: it explains how the two lists relate, not either one. */}
                                        {field.standardFieldIdentifier === ContractStandardField.CounterParties && (
                                            <DotLegalAlert severity="info" warningText={translateString("aiUpdateContractPartiesInfo")} marginTop={2} />
                                        )}
                                        <ContractAiUpdateFieldRow
                                            field={field}
                                            currentContract={state.currentContract!}
                                            proposedContract={state.review!.contract}
                                            currentTagIds={state.currentTagIds}
                                            proposedTagIds={state.review!.tagIds}
                                            citation={state.citationsByField(field)}
                                            canToggle={canToggle}
                                            isShowingCurrentValue={isShowingCurrentValue}
                                            validationErrors={state.validationErrors}
                                            onProposedChange={state.onProposedChange}
                                            onProposedTagsChange={state.onProposedTagsChange}
                                            onToggle={() => state.toggleField(field)}
                                            onShowInDocument={state.onShowInDocument}
                                            currentCounterPartiesSlot={
                                                <CounterPartiesReviewField
                                                    parties={state.currentParties}
                                                    selectableLegalEntities={state.selectableLegalEntities}
                                                    isLoading={state.isLoadingLegalEntities}
                                                    readOnly
                                                    onRemove={() => {}}
                                                    onToggleIncluded={() => {}}
                                                    onAdd={() => {}}
                                                    onShowInDocument={state.onShowInDocument}
                                                    getToggleState={state.getPartyToggleState}
                                                    onToggle={state.toggleCurrentParty}
                                                />
                                            }
                                            proposedCounterPartiesSlot={
                                                <CounterPartiesReviewField
                                                    parties={state.proposedParties}
                                                    selectableLegalEntities={state.selectableLegalEntities}
                                                    isLoading={state.isLoadingLegalEntities}
                                                    readOnly={false}
                                                    onRemove={state.onRemoveParty}
                                                    onToggleIncluded={state.onToggleParty}
                                                    onAdd={state.onAddParty}
                                                    onShowInDocument={state.onShowInDocument}
                                                    errorText={state.counterPartiesErrorText}
                                                />
                                            }
                                        />
                                        {field.standardFieldIdentifier === ContractStandardField.EndDate && (
                                            <ContractAiUpdateAutoRenewalRow
                                                endDateField={field}
                                                currentContract={state.currentContract!}
                                                proposedContract={state.review!.contract}
                                                canRevert={state.canRevertAutoRenewal}
                                                onProposedChange={state.onProposedChange}
                                                onRevert={state.revertAutoRenewal}
                                            />
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Fragment>
                    ))}
            </Box>

            {/* The proposed party list replaces rather than adds to the current one, so saving can drop a
                party the user never looked at. They are named rather than counted. */}
            {isConfirmingSave && (
                <DotLegalDialog
                    open
                    size="sm"
                    header={translateString("contractPartiesWillBeRemoved")}
                    buttonOkText={translateString("save")}
                    onOkClick={() => {
                        setIsConfirmingSave(false);
                        state.save();
                    }}
                    secondaryButtonText={translateString("cancel")}
                    onSecondaryButtonClick={() => setIsConfirmingSave(false)}
                    onDialogClose={() => setIsConfirmingSave(false)}
                >
                    {/* The header's own center only aligns text inside a box shrunk to that text, so it is centred from out here. */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <DotLegalHeader headerStyle="small" fontWeight={400} marginBottom={0} center>
                            {state.partiesToBeRemoved.join(", ")}
                        </DotLegalHeader>
                    </Box>
                </DotLegalDialog>
            )}
        </PaneLayout>
    );
}

export default defineDocumentReviewPane({
    slot: "right",
    component: ContractAiUpdatePane,
    reviewedDocumentId: (props) => props.documentId,
});
